import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, AlertCircle } from 'lucide-react';
import { productsData } from '../data/ecofinexData';
import QuoteFormModal from '../components/ecofinex/QuoteFormModal';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(10);
  const [error, setError] = useState('');
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const product = productsData.find(p => p.id === id);
  
  const MIN_ORDER = 10;

  // Memoize product details to avoid re-creating object on each render
  const productDetailsForQuote = useMemo(() => {
    if (!product) return null;
    return {
      shadeName: product.shadeName,
      productCode: product.productCode,
      finishType: product.finishType,
      quantity: quantity
    };
  }, [product, quantity]);

  useEffect(() => {
    if (!product) {
      navigate('/');
    }
  }, [product, navigate]);

  if (!product) return null;

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    setQuantity(value);
    
    if (value < MIN_ORDER && value > 0) {
      setError(`Minimum order quantity is ${MIN_ORDER}kg.`);
    } else {
      setError('');
    }
  };

  const handleGetQuote = () => {
    if (quantity < MIN_ORDER) {
      setError(`Minimum order quantity is ${MIN_ORDER}kg.`);
      return;
    }
    setIsQuoteModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={() => navigate('/shades')}
              data-testid="back-to-shades-btn"
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600 transition-colors font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Shades
            </button>
            <a href="/" className="flex items-center">
              <img 
                src="/ecofinex-logo.png" 
                alt="EcoFinex Logo" 
                className="h-10"
              />
            </a>
            <div className="w-24"></div>
          </div>
        </div>
      </nav>

      {/* Product Detail Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Color Swatch - Large */}
            <div className="relative">
              <div 
                className="aspect-square rounded-3xl shadow-xl overflow-hidden border border-gray-200"
                style={{ 
                  background: product.colorHex || '#ccc'
                }}
              >
                {/* Finish Badge */}
                <div className="absolute top-8 left-8">
                  <div className="px-6 py-3 bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-sm">
                    <p className="text-sm font-medium text-gray-900">{product.finishType} Finish</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Information */}
            <div className="flex flex-col justify-center">
              <div className="mb-8">
                <p className="text-sm text-gray-500 mb-2">{product.productCode}</p>
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">{product.shadeName}</h1>
                {product.ralCode && (
                  <p className="text-lg text-gray-600 mb-6">RAL {product.ralCode}</p>
                )}
              </div>

              {/* MOQ Info */}
              <div className="mb-8 p-6 bg-blue-50 rounded-2xl border border-blue-100">
                <div className="flex items-center gap-2 text-blue-800">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">Minimum Order Quantity: {MIN_ORDER}kg</span>
                </div>
                <p className="text-sm text-blue-600 mt-2">
                  Request a quote for competitive pricing on your order.
                </p>
              </div>

              {/* Quantity Selector */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Quantity (KG)
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => {
                      const newQty = Math.max(MIN_ORDER, quantity - 5);
                      setQuantity(newQty);
                      setError('');
                    }}
                    className="w-12 h-12 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 font-semibold transition-colors border border-gray-200"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min={MIN_ORDER}
                    value={quantity}
                    onChange={handleQuantityChange}
                    data-testid="quantity-input"
                    className="w-32 h-12 text-center text-lg font-semibold rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-colors text-gray-900 bg-white"
                  />
                  <button
                    onClick={() => {
                      setQuantity(quantity + 5);
                      setError('');
                    }}
                    className="w-12 h-12 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 font-semibold transition-colors border border-gray-200"
                  >
                    +
                  </button>
                </div>
                {error && (
                  <div className="mt-3 flex items-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                  </div>
                )}
              </div>

              {/* Get Quote Button */}
              <button
                onClick={handleGetQuote}
                disabled={quantity < MIN_ORDER}
                data-testid="get-quote-btn"
                className={`w-full py-4 rounded-2xl text-white font-medium text-lg transition-all duration-300 flex items-center justify-center gap-3 ${
                  quantity < MIN_ORDER
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 hover:shadow-xl hover:scale-105'
                }`}
              >
                <MessageSquare className="w-5 h-5" />
                Request Quote
              </button>

              {/* Product Details */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Product Details</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Finish Type: {product.finishType}</li>
                  <li>• Product Code: {product.productCode}</li>
                  {product.ralCode && <li>• RAL Code: {product.ralCode}</li>}
                  <li>• Minimum Order: {MIN_ORDER}kg</li>
                  <li>• Industrial grade quality</li>
                  <li>• Fast curing formula</li>
                  <li>• Excellent color consistency</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Modal */}
      <QuoteFormModal 
        isOpen={isQuoteModalOpen}
        onClose={() => setIsQuoteModalOpen(false)}
        productDetails={productDetailsForQuote}
      />
    </div>
  );
};

export default ProductDetail;
